import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, ButtonUI, CalendarInfo, EmptyState, LoadingSkeleton, Switch } from 'sonata-react-components';

import { normalizeCalendarInfo } from '@/helpers/common/date.helper';

import type { MultiFlightCalendarInfo } from 'sonata-static-storage/lib/common/common.types';

import './calendarSummary.scss';

type Props = {
  calendarInfo?: MultiFlightCalendarInfo;
  id: string;
  loading?: boolean;
  onSetFlightsClick?: () => void;
};

export function CalendarSummary({ calendarInfo, id, loading = false, onSetFlightsClick }: Props) {
  const { formatMessage } = useIntl();
  const normalizedCalendarInfo = calendarInfo ? normalizeCalendarInfo(calendarInfo) : undefined;

  return (
    <div className="calendarSummary" id={id}>
      <div className="calendarSummary__sectionHeader">
        <div className="calendarSummary__sectionTitleContainer">
          <h4 className="calendarSummary__title">
            {formatMessage({
              defaultMessage: 'IO Calendar Summary',
              id: 'calendarSummary.title'
            })}
          </h4>
          <h5 className="calendarSummary__subTitle">
            {formatMessage({
              defaultMessage: 'Customize ad display times for maximum impact',
              id: 'calendarSummary.subtitle'
            })}
          </h5>
        </div>
        <div className="calendarSummary__sectionActions">
          <ButtonUI
            id={`setFlightsHeaderButton-${id}`}
            onClick={onSetFlightsClick}
            shape="link"
            text={formatMessage({
              defaultMessage: 'Set Flights',
              id: 'calendarSummary.setFlightsButton'
            })}
            type="secondary"
          />
        </div>
      </div>
      <div className="calendarSummary__infoText">
        <i className="fa-regular fa-circle-info" id={`icon-infoText-${id}`} />{' '}
        {formatMessage({
          defaultMessage:
            'To determine which days your campaign will serve, you need to have at least one Line Item configured.',
          id: 'calendarSummary.infoText'
        })}
      </div>
      <Switch>
        <Switch.Case condition={!loading && Boolean(calendarInfo) && (calendarInfo?.availableDates?.length || 0) > 0}>
          <div className="calendarSummary__infoContainer">
            <div className="calendarSummary__calendarContainer">
              <CalendarInfo
                availableDates={normalizedCalendarInfo?.availableDates || []}
                id={`calendar-view-${id}`}
                selectedDates={normalizedCalendarInfo?.activeDates || []}
              />
            </div>
            <Accordion className="calendarSummary__legendAccordion" defaultOpen>
              <Accordion.Header className="calendarSummary__accordionHeader" hasBorder={true} isBold>
                {formatMessage({
                  defaultMessage: 'Legend',
                  id: 'calendarSummary.detailBox.title'
                })}
                <Accordion.Icon />
              </Accordion.Header>
              <Accordion.Content>
                <ul className="calendarSummary__legendList">
                  <li className="calendarSummary__legendItem">
                    <span className="calendarSummary__legendColor calendarSummary__legendColor--availableDays" />
                    <FormattedMessage
                      defaultMessage="<b>Available</b> days"
                      id={'calendarSummary.detailBox.legend.availableDays'}
                      values={{
                        b: (chunks: React.ReactNode) => (
                          <strong className="calendarSummary__legendBold">{chunks}</strong>
                        )
                      }}
                    />
                  </li>
                  <li className="calendarSummary__legendItem">
                    <span className="calendarSummary__legendColor calendarSummary__legendColor--unavailableDays" />
                    <FormattedMessage
                      defaultMessage="<b>Unavailable</b> due to unconfigured flights"
                      id={'calendarSummary.detailBox.legend.unavailableDays'}
                      values={{
                        b: (chunks: React.ReactNode) => (
                          <strong className="calendarSummary__legendBold">{chunks}</strong>
                        )
                      }}
                    />
                  </li>
                  <li className="calendarSummary__legendItem">
                    <span className="calendarSummary__legendColor calendarSummary__legendColor--activeDays" />
                    <FormattedMessage
                      defaultMessage="Days when <b>impressions are served</b> considering IO and Line Item Dayparting."
                      id={'calendarSummary.detailBox.legend.activeDays'}
                      values={{
                        b: (chunks: React.ReactNode) => (
                          <strong className="calendarSummary__legendBold">{chunks}</strong>
                        )
                      }}
                    />
                  </li>
                </ul>
              </Accordion.Content>
            </Accordion>
            <div className="calendarSummary__boxExtraData">
              <i className="fa-regular fa-circle-exclamation" id={`icon-extraData-${id}`} />
              {formatMessage({
                defaultMessage:
                  'Please remember that the configuration in Line Items overrides the configuration in the IO',
                id: 'calendarSummary.detailBox.extraData'
              })}
            </div>
          </div>
        </Switch.Case>
        <Switch.Case condition={loading}>
          <LoadingSkeleton count={1} height={500} />
        </Switch.Case>
        <Switch.Default>
          <EmptyState
            buttonProps={{
              onClick: onSetFlightsClick,
              text: formatMessage({
                defaultMessage: 'Set Flights',
                id: 'calendarSummary.setFlightsButton'
              })
            }}
            description={formatMessage({
              defaultMessage: 'Please configure your flights before using the calendar.',
              id: 'calendarSummary.emptyState.description'
            })}
            icon="fal fa-empty-set"
            id={`emptyState-${id}`}
            show
            title={formatMessage({
              defaultMessage: 'Calendar Unavailable',
              id: 'calendarSummary.emptyState.title'
            })}
          />
        </Switch.Default>
      </Switch>
    </div>
  );
}
